import { useAtom } from 'jotai'
import type { AppContext, AppProps } from 'next/app'
import App from 'next/app'
import { DefaultSeo } from 'next-seo'

import { themeModeAtom } from '@twisto/components/atoms/theme-provider'
import { BaseApp } from '@twisto/components/organisms/base-app'
import {
  Favicons,
  GtmInit,
  MetaTags,
} from '@twisto/components/organisms/head-tags'
import { parseLocale } from '@twisto/nextjs/locale'

import { getGlobalMetadata } from '../utils/api'

import type { DynamicPageProps } from './[[...path]]'

const MyApp = ({ Component, pageProps, router }: AppProps) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { region, lang } = parseLocale(router.locale as any)
  const { global } = pageProps as DynamicPageProps

  const { metaTitleSuffix = '', seo } = global?.data ?? {}

  const [, setThemeMode] = useAtom(themeModeAtom)

  setThemeMode('light')

  return (
    <>
      <DefaultSeo
        title={seo?.metaTitle}
        titleTemplate={`%s${metaTitleSuffix}`}
      />
      <MetaTags lang={lang} region={region} />
      <Favicons />
      <GtmInit region={region} />

      <BaseApp hasBrowserRouter={false} translationBundle="homepage">
        <Component {...pageProps} />
      </BaseApp>
    </>
  )
}

// getInitialProps disables automatic static optimization for pages that don't
// have getStaticProps. So [[...url]] pages still get SSG.
// Replace this with getStaticProps once this issue is fixed:
// https://github.com/vercel/next.js/discussions/10949
MyApp.getInitialProps = async (appContext: AppContext) => {
  // Calls page's `getInitialProps` and fills `appProps.pageProps`
  const appProps = await App.getInitialProps(appContext)

  const { data } = await getGlobalMetadata()

  return {
    ...appProps,
    pageProps: {
      global: data,
    },
  }
}

export default MyApp
